/**
 * foodCategories.js
 *
 * Shared multi-category membership helpers for the Food model's additive
 * `categories` array (ordered, first entry = primary; legacy `category`
 * scalar always mirrors `categories[0]`, see backend `Food.model.js`).
 * Every reader that groups/filters foods by category id MUST go through
 * these helpers instead of reading `food.category`/`food.categories`
 * directly — `categories` is NOT guaranteed to exist on old documents (the
 * backfill migration is written but not run), so a bare `food.categories`
 * read silently returns `undefined` for most of the current catalog.
 *
 * @module helpers/foodCategories
 */

/** Unwrap one category ref — a populated `{_id}` object, an ObjectId, or a
 * bare string — down to its id string. */
const idOf = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'object') return String(value._id || value.id || value.value || value);
    return String(value);
};

/**
 * Resolve every category id a food belongs to, as strings, in the food's own
 * order (primary first).
 *
 * - `categories` non-empty wins outright (mirrors it verbatim, unwrapped).
 * - Otherwise falls back to `[category]` when `category` is set.
 * - Otherwise `[]` (uncategorised).
 *
 * @param {object} food a raw or populated food document.
 * @returns {string[]} ordered category id strings (possibly empty).
 */
export const foodCategoryIds = (food) => {
    const categories = Array.isArray(food?.categories) ? food.categories : [];
    if (categories.length) {
        return categories.map(idOf).filter(Boolean);
    }
    const primary = idOf(food?.category);
    return primary ? [primary] : [];
};

/**
 * Whether `food` belongs to the given category id — `categories` membership
 * when present, else a single `category` equality check.
 *
 * @param {object} food a raw or populated food document.
 * @param {string} categoryId the category id to test membership against.
 * @returns {boolean}
 */
export const foodBelongsToCategory = (food, categoryId) => {
    const id = idOf(categoryId);
    if (!id) return false;
    return foodCategoryIds(food).includes(id);
};

/**
 * Per-catalog category index: `categoryId -> sellable foods` (visible AND
 * enabled), each list in catalog order. Weakly keyed by the catalog array, so a
 * re-synced catalog drops the old index and every category tap after the first
 * is an O(1) lookup instead of re-scanning (and re-normalising the category refs
 * of) every product — which with 80 000 products is what made opening a
 * category stall.
 */
const sellableByCategoryCache = new WeakMap();

/**
 * Sellable (visible + enabled) foods of one category, via a cached index.
 *
 * @param {object[]} foods the catalog array (identity is the cache key).
 * @param {string} categoryId the category id.
 * @returns {object[]} foods in catalog order (shared array — do not mutate).
 */
export const getSellableFoodsForCategory = (foods, categoryId) => {
    if (!Array.isArray(foods)) return [];
    const id = idOf(categoryId);
    if (!id) return [];

    let index = sellableByCategoryCache.get(foods);
    if (!index) {
        index = new Map();
        foods.forEach((food) => {
            if (!food || food.visible !== true || food.enabled !== true) return;
            foodCategoryIds(food).forEach((foodCategoryId) => {
                const bucket = index.get(foodCategoryId);
                if (bucket) {
                    bucket.push(food);
                } else {
                    index.set(foodCategoryId, [food]);
                }
            });
        });
        sellableByCategoryCache.set(foods, index);
    }

    return index.get(id) || [];
};
